import type { PracticeWeekday } from '@region13/shared-types'
import { UPPER_DIVISION_INTRO } from '../lib/practicePrefsCopy'
import { DayPreferencePicker } from './DayPreferencePicker'

/** Just the day-ranking part of a UD team's draft. Narrower than `PrefsDraft`
 *  on purpose: this component has no business with times, which UD is not
 *  asked for. */
export interface UpperDivisionDraft {
  dayPref: PracticeWeekday[]
  dayPref2: PracticeWeekday[] | null
  dayBlocked: PracticeWeekday[]
}

/** Drops any day that just became blocked from a ranked list. `cycleDay`
 *  (which each `DayPreferencePicker` calls on its own click) only maintains
 *  the ranked-XOR-blocked invariant WITHIN the `DayState` it is handed - it
 *  cannot see the other picker's ranked array. Blocking a day in one of UD's
 *  two lists has to clear it from the other too, or a day can end up
 *  "ranked" in one list and "does not work" in the shared blackout set at
 *  once, which is exactly what the one-shared-blackout-set rule exists to
 *  prevent. Ranking the same day in both lists (without blocking it) is not
 *  touched by this - only a day that is actually in `blocked` is stripped. */
function stripBlockedDays(ranked: PracticeWeekday[], blocked: PracticeWeekday[]): PracticeWeekday[] {
  return ranked.filter((d) => !blocked.includes(d))
}

/** `null` when this click's `blocked` didn't touch the OTHER list at all, or
 *  the other list with the newly-blocked day(s) removed when it did. Kept as
 *  an explicit no-op case (rather than always handing back a value) so a
 *  click that only ever touched its own list still produces the same
 *  two-key patch a plain `DayPreferencePicker` onChange always has -
 *  `{ dayPref: ..., dayBlocked: ... }` - with no incidental third key. */
function reconcileOther(other: PracticeWeekday[], blocked: PracticeWeekday[]): PracticeWeekday[] | null {
  const stripped = stripBlockedDays(other, blocked)
  return stripped.length === other.length ? null : stripped
}

/** The Upper Division variant of the day questions (spec D4): two ranked
 *  lists, one per weekly practice, over ONE shared blackout set. A day that
 *  does not work does not work for either practice, so two independent
 *  blackout sets would let a coach contradict themselves. */
export function UpperDivisionFields({
  draft,
  onPatch,
  reachable,
}: {
  draft: UpperDivisionDraft
  onPatch: (fields: Partial<UpperDivisionDraft>) => void
  reachable: Set<number>
}) {
  return (
    <>
      <p className="text-sm text-ink-soft">{UPPER_DIVISION_INTRO}</p>
      <DayPreferencePicker
        label="Preferred days for the first practice"
        value={{ ranked: draft.dayPref, blocked: draft.dayBlocked }}
        onChange={(v) => {
          const strippedSecond = reconcileOther(draft.dayPref2 ?? [], v.blocked)
          onPatch(
            strippedSecond === null
              ? { dayPref: v.ranked, dayBlocked: v.blocked }
              : { dayPref: v.ranked, dayPref2: strippedSecond, dayBlocked: v.blocked },
          )
        }}
        reachable={reachable}
      />
      <DayPreferencePicker
        label="Preferred days for the second practice"
        value={{ ranked: draft.dayPref2 ?? [], blocked: draft.dayBlocked }}
        onChange={(v) => {
          const strippedFirst = reconcileOther(draft.dayPref, v.blocked)
          onPatch(
            strippedFirst === null
              ? { dayPref2: v.ranked, dayBlocked: v.blocked }
              : { dayPref2: v.ranked, dayPref: strippedFirst, dayBlocked: v.blocked },
          )
        }}
        reachable={reachable}
      />
    </>
  )
}
